import { useQuery } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';
import { useMeusTermos } from '@/modulos/portal-credenciado/contratos/dados';

import type { LinhaDaProjecao, OrdemDoCredenciado, RespostaDaProjecao } from './contrato';

/**
 * Camada de dados das Ordens de Serviço do credenciado. Nenhum componente
 * busca dado direto, e não há mock aqui: OS nunca entra na exceção de dado
 * simulado do CLAUDE.md.
 *
 * A rota exige Termo ativo (`@ExigeTermoAtivo`): sem ele, responde 403. Por
 * isso a consulta só sai quando "Meus termos" traz um termo `ATIVO`, e o 403
 * que ainda vier (termo revogado entre uma consulta e outra) vira o mesmo
 * estado vazio explicativo — nunca tela de falha.
 *
 * A API não pagina nem tem detalhe por id: devolve a coleção inteira. Lista,
 * agrupamento por termo e detalhe dividem a mesma consulta, cada um com o seu
 * `selecionar`.
 */
export const CAMINHO_DA_API = '/bff/v1/j06-s03-projecao-credenciado/state';

export const CHAVES_DE_OS = {
  todas: ['credenciado', 'ordens-servico'] as const,
};

/** Texto fixo do vazio sem Termo ativo (ativos canônicos, seção 5). */
export const TEXTO_SEM_TERMO_ATIVO =
  'As Ordens de Serviço aparecem aqui quando o Termo estiver ativo.';

/** A linha da projeção no formato da tela. O que a linha não traz fica `null`. */
export function paraAOrdem(linha: LinhaDaProjecao): OrdemDoCredenciado {
  const snapshot = linha.snapshot ?? null;
  const recursos = snapshot?.recursos ?? null;
  return {
    id: linha.ordemServicoId,
    numero: linha.numero,
    numeroCompleto: linha.numeroCompleto ?? null,
    status: linha.status,
    inicio: linha.inicio,
    fim: linha.fim,
    termoId: snapshot?.termo?.id ?? null,
    placa: linha.placa ?? recursos?.veiculo?.placa ?? null,
    crm: recursos?.veiculo?.crm ?? null,
    modalidade: linha.modalidade ?? snapshot?.modalidade ?? null,
    dre: linha.dre ?? snapshot?.dre ?? null,
    condutor: linha.condutor ?? recursos?.condutor?.nome ?? null,
    monitor: linha.monitor ?? recursos?.monitor?.nome ?? null,
    escolas: snapshot?.escolas ?? [],
  };
}

/**
 * As OS de cada Termo de Adesão, pela chave `snapshot.termo.id`, na ordem em
 * que a API devolve (ativação mais recente primeiro). OS sem termo no
 * snapshot não entra em grupo nenhum: toda função que grava o snapshot põe o
 * termo nele (ver `contrato.ts`), então a falta seria defeito do dado, e
 * pendurá-la num termo qualquer afirmaria o que ninguém apurou.
 */
export function agruparPorTermo(
  ordens: readonly OrdemDoCredenciado[],
): Map<string, OrdemDoCredenciado[]> {
  const grupos = new Map<string, OrdemDoCredenciado[]>();
  for (const ordem of ordens) {
    if (!ordem.termoId) continue;
    const grupo = grupos.get(ordem.termoId);
    if (grupo) grupo.push(ordem);
    else grupos.set(ordem.termoId, [ordem]);
  }
  return grupos;
}

/**
 * O que a tela precisa para os quatro estados, já decidido. Cabe direto no
 * `EstadoDaTela` (`carregando`, `erro`, `aoTentarDeNovo`); o vazio é da tela.
 */
export interface ConsultaDeOrdens<R> {
  /** Termos ou projeção na primeira busca. */
  carregando: boolean;
  /** Falha que a tela mostra como falha. Nunca o 403 da falta de Termo ativo. */
  erro: unknown;
  /**
   * Sem Termo ativo — "Meus termos" não traz nenhum, ou a API recusou com 403.
   * A tela mostra `TEXTO_SEM_TERMO_ATIVO`; a projeção não foi consultada (ou
   * foi recusada).
   */
  semTermoAtivo: boolean;
  /** O resultado do `selecionar`; `undefined` enquanto carrega, na falha e sem termo ativo. */
  dados: R | undefined;
  tentarDeNovo: () => void;
}

function ehRecusa(erro: unknown): boolean {
  return erro instanceof ErroApi && erro.pedeOutroCaminho;
}

/**
 * Todas as OS do credenciado, passadas pelo `selecionar`. P5 usa
 * `useOrdensDoCredenciado((o) => o.length)` para o indicador do painel.
 */
export function useOrdensDoCredenciado<R>(
  selecionar: (ordens: OrdemDoCredenciado[]) => R,
): ConsultaDeOrdens<R> {
  const termos = useMeusTermos();
  const haTermoAtivo = termos.data?.some((termo) => termo.status === 'ATIVO') ?? false;

  const consulta = useQuery<RespostaDaProjecao, ErroApi, R>({
    queryKey: CHAVES_DE_OS.todas,
    queryFn: ({ signal }) => chamarApi<RespostaDaProjecao>(CAMINHO_DA_API, { sinal: signal }),
    select: (resposta) => selecionar((resposta.state.ordens ?? []).map(paraAOrdem)),
    retry: naoInsistirEmRecusa,
    enabled: haTermoAtivo,
  });

  const vazio = { dados: undefined, carregando: false, erro: null } as const;

  if (termos.isPending) {
    return { ...vazio, carregando: true, semTermoAtivo: false, tentarDeNovo: noop };
  }
  if (termos.error) {
    // Quem não alcança "Meus termos" (403) também não alcança a projeção.
    if (ehRecusa(termos.error)) return { ...vazio, semTermoAtivo: true, tentarDeNovo: noop };
    return {
      ...vazio,
      erro: termos.error,
      semTermoAtivo: false,
      tentarDeNovo: () => void termos.refetch(),
    };
  }
  if (!haTermoAtivo || ehRecusa(consulta.error)) {
    return { ...vazio, semTermoAtivo: true, tentarDeNovo: noop };
  }
  return {
    carregando: consulta.isPending,
    erro: consulta.error ?? null,
    semTermoAtivo: false,
    dados: consulta.data,
    tentarDeNovo: () => void consulta.refetch(),
  };
}

function noop(): void {}

/**
 * As OS de um termo. Termo que não está `ATIVO` mostra o vazio explicativo,
 * mesmo quando outro termo do credenciado está ativo e a projeção foi lida —
 * a menos que a projeção traga OS dele, e então elas aparecem.
 */
export function useOrdensPorTermo(termoId: string): ConsultaDeOrdens<OrdemDoCredenciado[]> {
  const termos = useMeusTermos();
  const termo = termos.data?.find((t) => t.id === termoId);
  const consulta = useOrdensDoCredenciado((ordens) =>
    ordens.filter((ordem) => ordem.termoId === termoId),
  );

  if (termo && termo.status !== 'ATIVO' && !consulta.semTermoAtivo) {
    const proprias = consulta.dados ?? [];
    if (proprias.length === 0) {
      return { ...consulta, carregando: false, erro: null, dados: undefined, semTermoAtivo: true };
    }
  }
  return consulta;
}

/** As OS do credenciado agrupadas por termo (`agruparPorTermo`). */
export function useOrdensAgrupadasPorTermo(): ConsultaDeOrdens<Map<string, OrdemDoCredenciado[]>> {
  return useOrdensDoCredenciado(agruparPorTermo);
}

/**
 * Uma OS pelo id, para o detalhe (P4). `null` quando o id não está na
 * projeção: o React Query recusa `undefined` como dado e trataria o "não
 * encontrado" como falha de carregamento.
 */
export function useOrdemDoCredenciado(
  id: string | undefined,
): ConsultaDeOrdens<OrdemDoCredenciado | null> {
  return useOrdensDoCredenciado((ordens) => ordens.find((ordem) => ordem.id === id) ?? null);
}
